import { Badge } from '../../../components/ui';
import { useT } from '../../../i18n';
import { KHONG_CO, ngayVN, soVN } from '../../../lib/dinh-dang';
import { BADGE_HIEU_LUC, BADGE_KET, TRANG_THAI_NAP } from '../constants';
import { ngayHomNay, tinhHieuLuc } from '../nghiep-vu/tri-thuc';
import type { KnowledgeFile } from '../types';

/** Ô "Hiệu lực": khoảng ngày, kèm nhãn khi hôm nay nằm ngoài khoảng đó. */
export function OHieuLuc({ tep }: { tep: Pick<KnowledgeFile, 'effective_from' | 'effective_to'> }) {
  const { t } = useT();
  const tinhTrang = tinhHieuLuc(tep.effective_from, tep.effective_to, ngayHomNay());
  if (tinhTrang === 'khongDat') return <span className="text-xs text-content-muted">{KHONG_CO}</span>;
  const nhan = tinhTrang === 'het' || tinhTrang === 'chuaToi' ? BADGE_HIEU_LUC[tinhTrang] : undefined;
  return (
    <div className="flex flex-col items-start gap-1">
      <span className="whitespace-nowrap text-xs text-content-secondary">
        {t('triThuc.tep.khoangHieuLuc', {
          tu: tep.effective_from ? ngayVN(tep.effective_from) : KHONG_CO,
          den: tep.effective_to ? ngayVN(tep.effective_to) : t('triThuc.tep.khongThoiHan'),
        })}
      </span>
      {nhan && <Badge variant={nhan.variant}>{t(nhan.khoa)}</Badge>}
    </div>
  );
}

/**
 * Ô "Trạng thái nạp": nhãn của hàng đợi trong module, cộng nhãn "Kẹt" khi tệp chờ quá hạn, và câu lỗi hay
 * cảnh báo index trả về (nguyên văn, không dịch — ADR-UI-0010 mục 5).
 */
export function OTrangThaiNap({ tep }: { tep: KnowledgeFile }) {
  const { t, locale } = useT();
  const cauHinh = TRANG_THAI_NAP[tep.index_status];
  const phu = !tep.has_file
    ? t('triThuc.nap.chiThongTin')
    : tep.index_status === 'LOI' && tep.index_attempts > 1
      ? t('triThuc.nap.soLanThu', { so: soVN(tep.index_attempts, locale) })
      : null;
  return (
    <div className="flex min-w-0 max-w-56 flex-col items-start gap-1">
      <span className="flex flex-wrap items-center gap-1">
        <Badge variant={cauHinh.variant}>{t(cauHinh.khoa)}</Badge>
        {tep.index_stuck && <Badge variant={BADGE_KET.variant}>{t(BADGE_KET.khoa)}</Badge>}
      </span>
      {tep.index_error && (
        <span className="line-clamp-2 break-words text-2xs text-danger-700" title={tep.index_error}>
          {tep.index_error}
        </span>
      )}
      {tep.index_warning && (
        <span className="line-clamp-2 break-words text-2xs text-warning-700" title={tep.index_warning}>
          {tep.index_warning}
        </span>
      )}
      {phu && <span className="text-2xs text-content-muted">{phu}</span>}
    </div>
  );
}

/**
 * Ô "Phân quyền": ai được trợ lý trả lời bằng tệp này. Phải qua cả hai cổng (`SQL_DUOC_DOC` của backend):
 * người dùng nằm trong danh sách người được đọc của tệp, **và** phòng ban của họ được dùng tệp (đặt riêng ở
 * tệp, không thì theo bộ). Chưa chọn người nào thì chỉ người có quyền đọc mọi tệp dùng được.
 */
export function OPhanQuyen({ tep }: { tep: KnowledgeFile }) {
  const { t, locale } = useT();
  const tenPhongBan = tep.permitted_departments.map((p) => p.name ?? KHONG_CO).join(', ');
  return (
    <div className="flex min-w-0 max-w-48 flex-col gap-0.5 text-xs">
      <span className={tep.user_permissions_configured ? 'truncate text-content-secondary' : 'truncate text-warning-700'}>
        {t(tep.user_permissions_configured ? 'triThuc.quyen.nguoiDaChon' : 'triThuc.quyen.chuaChonNguoi')}
      </span>
      <span className="truncate text-content-muted" title={tep.dept_permissions_configured ? tenPhongBan : undefined}>
        {!tep.dept_permissions_configured
          ? t('triThuc.quyen.phongBanTheoBo')
          : tep.permitted_departments.length === 0
            ? t('triThuc.quyen.khongPhongBanNao')
            : t('triThuc.quyen.soPhongBan', { so: soVN(tep.permitted_departments.length, locale) })}
      </span>
    </div>
  );
}
